<script setup lang="ts">
import { computed, ref } from 'vue'
import { Dialog, DialogPanel } from '@headlessui/vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useMainStore } from '~/store/MainStore'
import { getHoursFromAndTo, getOrderedHours } from '~/classes/HourUtils'
import BusinessHourSchedule
  from '~/components/Location/BusinessHourSchedule.vue'

const { t } = useI18n()
const mainStore = useMainStore()
const { currentLocation, isLocationOpen, isOrderingEnabled } = storeToRefs(mainStore)
const isOpen = ref(false)
const businessHours = computed(() => {
  return currentLocation.value && currentLocation.value.businessHours
    ? getOrderedHours(currentLocation.value.businessHours, mainStore.timeZone) || []
    : []
})
const orderingHours = computed(() => {
  return currentLocation.value && currentLocation.value.orderingHours
    ? getOrderedHours(currentLocation.value.orderingHours, mainStore.timeZone) || []
    : []
})
function setIsOpen() {
  isOpen.value = !isOpen.value
}
</script>

<template>
  <div class="inline-flex relative">
    <div v-if="currentLocation && isLocationOpen">
      {{ t('OPEN') }}
      <span v-if="isLocationOpen && isOrderingEnabled">
        &bull; {{ getHoursFromAndTo(currentLocation.orderingHours, t, mainStore.timeZone) }}
      </span>
      <span v-else-if="isLocationOpen">
        &bull; {{ getHoursFromAndTo(currentLocation.businessHours, t, mainStore.timeZone) }}
      </span>
    </div>
    <div v-else>
      {{ t('CLOSED') }}
    </div>
    <div class="sm:ml-2 sm:pl-2 ml-1.5 pl-1.5 border-l border-tw-1">
      <button
        type="button"
        class="tw-text-button sm:text-sm text-xs"
        @click="setIsOpen()"
      >
        {{ t('FULL_HOURS') }}
      </button>
    </div>
    <ClientOnly>
      <Dialog
        :open="isOpen"
        as="div"
        class="relative z-50"
        @close="setIsOpen"
      >
        <!-- The backdrop, rendered as a fixed sibling to the panel container -->
        <div
          class="fixed inset-0 backdrop-blur"
          aria-hidden="true"
        />
        <!-- Full-screen container to center the panel -->
        <div class="fixed inset-0 flex w-screen items-center justify-center">
          <!-- The actual dialog panel -->
          <DialogPanel class="w-full lg:max-w-4xl md:max-w-2xl sm:max-w-xl max-w-md bg-tw-2 text-tw-1 rounded-md p-5">
            <div class="w-full flex flex-row justify-end">
              <button
                type="button"
                @click="setIsOpen()"
              >
                <span class="absolute -inset-0.5" />
                <span class="sr-only">{{ t('CLOSE') }}</span>
                <XMarkIcon
                  class="h-6 w-6"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div class="w-full grid sm:grid-cols-2 grid-cols-1 gap-5 text-tw-1">
              <div
                v-if="currentLocation"
                class="col-span-1"
              >
                <div class="font-semibold mb-2 text-base capitalize">
                  {{ t('ORDERING_HOURS') }}
                </div>
                <business-hour-schedule :days="orderingHours" />
              </div>
              <div
                class="col-span-1"
              >
                <div class="font-semibold mb-2 text-base capitalize">
                  {{ t('BUSINESS_HOURS') }}
                </div>
                <business-hour-schedule :days="businessHours" />
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </ClientOnly>
  </div>
</template>
